import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from "motion/react";
import { FolderOpen, ArrowRight } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { usePageMetadata } from '../../hooks/usePageMetadata';
import apiClient from '../../api/axios';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';

interface ProjectDto {
    id: string;
    slug: string;
    icon?: string;
    imageUrl?: string;
    color?: string;
    title: string;
    description: string;
}

export const Projekte: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    // Fetch page metadata and update title
    usePageMetadata('projekte');

    const { data: pagedData, isLoading } = useQuery({
        queryKey: ['publicProjects'],
        queryFn: async () => {
            const response = await apiClient.get('/public/projects');
            return response.data;
        }
    });

    const projects: ProjectDto[] = pagedData?.data || [];

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.15,
            },
        },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0 },
    };

    return (
        <div className="flex flex-col py-16 sm:py-24 bg-background min-h-screen">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 w-full">

                <nav className="mb-8 text-sm font-medium text-foreground/60 flex items-center gap-2">
                    <Link to="/" className="hover:text-primary transition-colors">{t('common.home')}</Link>
                    <span>/</span>
                    <span className="text-foreground">{t('projects.breadcrumb')}</span>
                </nav>

                {/* Header */}
                <div className="mx-auto max-w-2xl text-center mb-16">
                    <motion.h1
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl"
                    >
                        {t('projects.title')}
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="mt-4 text-lg leading-8 text-foreground/70"
                    >
                        {t('projects.description')}
                    </motion.p>
                </div>

                {/* Project Grid */}
                {isLoading ? (
                    <div className="flex justify-center items-center py-20">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                    </div>
                ) : projects && projects.length > 0 ? (
                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-100px" }}
                        className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:gap-12"
                    >
                        {projects.map((project: ProjectDto) => (
                            <motion.div
                                key={project.id}
                                variants={itemVariants}
                                onClick={() => navigate(`/projekte/${project.slug}`)}
                                className="group relative flex flex-col items-start justify-between rounded-3xl border border-border bg-background p-8 shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg cursor-pointer"
                            >
                                <div className="flex items-center gap-x-4">
                                    <div
                                        className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 transition-colors group-hover:bg-primary/20 overflow-hidden"
                                        style={(!project.imageUrl && project.color) ? { backgroundColor: project.color + '20' } : undefined}
                                    >
                                        {project.imageUrl ? (
                                            <img src={resolveAssetUrl(project.imageUrl)} alt={project.title} className="w-full h-full object-cover" />
                                        ) : (
                                            <FolderOpen className="h-7 w-7 text-primary" style={project.color ? { color: project.color } : undefined} />
                                        )}
                                    </div>
                                    <h3 className="text-xl font-bold text-foreground group-hover:text-primary transition-colors">
                                        {project.title}
                                    </h3>
                                </div>
                                <div className="mt-6 text-base leading-7 text-foreground/80 flex-1 line-clamp-3">
                                    {project.description?.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim()}
                                </div>
                                <div className="mt-8 flex items-center justify-end w-full border-t border-border/50 pt-6">
                                    <span className="text-sm font-semibold text-primary group-hover:text-primary-foreground group-hover:bg-primary px-4 py-2 rounded-lg transition-colors border border-transparent group-hover:border-primary flex items-center gap-2">
                                        {t('projects.learn_more')} <ArrowRight className="w-4 h-4" />
                                    </span>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                ) : (
                    <div className="rounded-3xl border border-dashed border-border bg-secondary/30 p-16 text-center">
                        <h3 className="text-lg font-medium text-foreground">{t('projects.no_projects')}</h3>
                        <p className="mt-2 text-foreground/60">{t('projects.check_back')}</p>
                    </div>
                )}
            </div>
        </div>
    );
};
